// Cliente da API do painel (TELAS §3). Cookie de sessão vai sozinho; toda mutação leva `X-Nexus: 1`. Erro do servidor
// chega como `{erro:{codigo,mensagem}}` e vira `ErroApi` — a tela mostra a mensagem, que já está escrita pro Arthur ler.
export class ErroApi extends Error {
  constructor(
    public readonly codigo: string,
    mensagem: string,
    public readonly status: number,
    public readonly detalhes?: unknown,
  ) {
    super(mensagem);
    this.name = 'ErroApi';
  }
}

export type Metodo = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Avisa o App que não há mais sessão: ele troca a casca pela tela de entrar. */
export const EVENTO_SAIU = 'nexus:saiu';

async function pedir<T>(metodo: Metodo, caminho: string, corpo?: unknown): Promise<T> {
  const mutacao = metodo !== 'GET';
  const resposta = await fetch(caminho.startsWith('/api') ? caminho : `/api${caminho}`, {
    method: metodo,
    credentials: 'same-origin',
    headers: {
      ...(mutacao ? { 'x-nexus': '1' } : {}),
      ...(corpo !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    ...(corpo !== undefined ? { body: JSON.stringify(corpo) } : {}),
  });

  if (resposta.status === 204) return undefined as T;
  const texto = await resposta.text();
  const dados: unknown = texto ? JSON.parse(texto) : null;

  if (!resposta.ok) {
    const e = (dados as { erro?: { codigo?: string; mensagem?: string; detalhes?: unknown } } | null)?.erro;
    // 09/10/2026 (achado 39): a sessão acabou (saiu, expirou, apagada noutro lugar): o App volta pro login, como o
    // sessaoCaiu do Atlas. Só 'sem_sessao': o 401 de senha errada (trocar senha) não derruba ninguém.
    if (resposta.status === 401 && e?.codigo === 'sem_sessao') window.dispatchEvent(new Event(EVENTO_SAIU));
    throw new ErroApi(e?.codigo ?? 'erro', e?.mensagem ?? 'deu ruim aqui', resposta.status, e?.detalhes);
  }
  return dados as T;
}

export const api = {
  get: <T>(caminho: string): Promise<T> => pedir<T>('GET', caminho),
  post: <T>(caminho: string, corpo?: unknown): Promise<T> => pedir<T>('POST', caminho, corpo ?? {}),
  put: <T>(caminho: string, corpo?: unknown): Promise<T> => pedir<T>('PUT', caminho, corpo ?? {}),
  patch: <T>(caminho: string, corpo?: unknown): Promise<T> => pedir<T>('PATCH', caminho, corpo ?? {}),
  delete: <T>(caminho: string): Promise<T> => pedir<T>('DELETE', caminho),
};

/** Monta query string ignorando o que está vazio — filtro não escolhido não vira `?x=undefined`. */
export function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const partes = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return partes.length > 0 ? `?${partes.join('&')}` : '';
}
